<template>
    <div
        ref="container"
        :class="{
            [props.emptyClass || '']: props.emptyClass && !isBannerVisible && !slotConfig.placeholderHeight,
            [props.backgroundClass || '']: props.backgroundClass && (!adDisabledStore.isAdDisabled || showAdFreeBanner),
        }"
        :style="{
            '--slot-max-height': slotHeight ? `${slotHeight}px` : 'auto',
        }"
    >
        <div
            :class="$style.wrapper"
            :style="{
                '--placeholder-height': slotConfig.placeholderHeight ? `${slotConfig.placeholderHeight}px` : 'auto',
            }"
        >
            <div
                v-if="
                    (appStore.isDebugMode || userStore.isCompanyEmployeeLoggedIn) &&
                    banner?.state === PageAdState.showing
                "
                :class="$style.wrapper_debug"
            >
                {{ banner?.bidderCode ? `bidder: ${banner.bidderCode}` : 'no bidder' }}
            </div>
            <AdFreeBanner
                v-if="showAdFreeBanner"
                :placement="props.placement"
            />
            <AdNativeContainer
                v-else-if="adsStore.nativeBannersEnabled"
                :placement="props.placement"
                @update:visible="onNativeVisibleChange"
            />
            <div
                v-else
                :id="id"
                :class="$style.container"
            />
            <slot :isVisible="isBannerVisible" />
        </div>
    </div>
</template>
<script lang="ts" setup>
import { useResizeObserver, useThrottleFn } from '@vueuse/core'
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, useId, useTemplateRef, watch } from 'vue'

import { getNativeBannerSlot } from '@/modules/adv/native-banners'
import { PageAdState, type PageAd } from '@/modules/adv/page-ad'
import { getPageAdSizesForSlot } from '@/modules/adv/page-ad-sizes'
import type { PageAdSize, PageAdType } from '@/modules/adv/types'
import { useAdDisabledStore } from '@/store/ad-disabled-store'
import { useAdsStore } from '@/store/ads-store'
import { useAppStore } from '@/store/app-store.js'
import { useUserStore } from '@/store/user-store/user-store.js'
import { idleCallbackPromise } from '@/utils/helpers'

import AdFreeBanner from './AdFreeBanner.vue'
import AdNativeContainer from './AdNativeContainer.vue'
import { bannersConfig, EXISTING_AD_FREE_BANNERS } from './config'

const adsStore = useAdsStore()
const appStore = useAppStore()
const userStore = useUserStore()
const adDisabledStore = useAdDisabledStore()

type Props = {
    placement: PageAdType
    /**
     * Class to add when the ad is not loaded
     * Should not hide element with "display: none", otherwise the ad will never be loaded
     */
    emptyClass?: string
    backgroundClass?: string
    respectHeight?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
    (e: 'update:visible', value: boolean): void
}>()
const slotConfig = computed(() => bannersConfig[props.placement])

const showAdFreeBanner = computed(
    () => adDisabledStore.isAdFreeActive && EXISTING_AD_FREE_BANNERS[props.placement] !== undefined,
)

// The native box may be taller than the web waterfall for the same slot, and `--slot-max-height`
// caps the wrapper — reading the web sizes there would clip the overlay's reserved space.
const slotHeight = computed(() => {
    const nativeSlot = adsStore.nativeBannersEnabled ? getNativeBannerSlot(props.placement) : undefined
    const sizes = nativeSlot?.sizes ?? slotConfig.value.sizes
    const heights = sizes.filter((s) => s !== 'fluid').map(([, h]) => h)
    return heights.length ? Math.max(...heights) : undefined
})

const banner = shallowRef<PageAd>()
const isBannerVisible = ref(false)

const id = useId()
const containerRef = useTemplateRef('container')
const lastFilteredSizes = ref<PageAdSize[]>()

function onNativeVisibleChange(value: boolean) {
    isBannerVisible.value = value
    emit('update:visible', value)
}

async function updateBanner() {
    await idleCallbackPromise()

    if (!adsStore.adService || !containerRef.value || adDisabledStore.isAdDisabled || adsStore.nativeBannersEnabled) {
        return
    }

    const rect = containerRef.value.getBoundingClientRect()
    const filteredSizes = getPageAdSizesForSlot(rect, {
        respectHeight: props.respectHeight,
        sizes: slotConfig.value.sizes,
    })
    // do nothing, if the max size is the same
    if (JSON.stringify(filteredSizes) === JSON.stringify(lastFilteredSizes.value)) {
        return
    }

    lastFilteredSizes.value = filteredSizes

    // destroy the previous banner
    banner.value?.destroy()
    banner.value = undefined

    // prepare a new banner if there are sizes
    if (filteredSizes.length > 0 && rect.width > 0) {
        banner.value = adsStore.adService.requestPageAd({
            placement: props.placement,
            refresh: slotConfig.value.refreshInterval,
            el: id,
            sizes: filteredSizes,
        })

        banner.value.addEventListener('rendered', () => {
            isBannerVisible.value = true
            emit('update:visible', true)
        })
        banner.value.addEventListener('empty', () => {
            isBannerVisible.value = false
            emit('update:visible', false)
        })
    }
}

const updateBannerThrottled = useThrottleFn(updateBanner, 1000)

async function checkEmptyStyles() {
    if (!props.emptyClass) {
        return
    }

    await idleCallbackPromise()

    const elem = containerRef.value?.appendChild(document.createElement('div'))
    if (elem) {
        elem.classList.add(props.emptyClass)
        if (window.getComputedStyle(elem).display === 'none') {
            console.error(
                `AdBlock: class .${props.emptyClass} sets display to "none". That breaks tracking of the future size changes.\n\nUse negative margins to hide empty ad in the layout.`,
            )
        }
        elem.remove()
    }
}

onMounted(() => {
    checkEmptyStyles()
    updateBannerThrottled()
})

watch(
    () => props.emptyClass,
    () => {
        checkEmptyStyles()
    },
)

watch(
    () => adDisabledStore.isAdDisabled,
    (value) => {
        // destroy the previous banner
        banner.value?.destroy()
        banner.value = undefined
        if (!value) {
            updateBannerThrottled()
        }
    },
)

useResizeObserver(containerRef, updateBannerThrottled)

onBeforeUnmount(() => {
    banner.value?.destroy()
})
</script>

<style module>
.wrapper {
    width: fit-content;
    margin: 0 auto;
    position: relative;
    pointer-events: none;
    min-height: var(--placeholder-height);
    max-height: var(--slot-max-height);
}

.wrapper_debug {
    position: absolute;
    bottom: 100%;
    left: 0;
    font-size: 10px;
}

/* fluid ads has inline style with "width: 100%". In that case wrapper should also take all space */
.wrapper:has(div[style*='width: 100%;']) {
    width: 100%;
}

.container {
    /* TODO: check ad requirements and remove if not needed */
    min-width: 1px;
    min-height: 1px;
    pointer-events: auto;
    overflow: hidden;
    max-height: var(--slot-max-height);
}
</style>
